// @flow
import { put, call, fork, all, take } from 'redux-saga/effects';
import { success, failure, request, action } from 'app/actions';
import * as appSagas from 'app/appSagas';
import * as loginSagas from 'login/loginSagas';
import * as classesStudentsSagas from 'classes-students/classesStudentsSagas';
import * as editClassSagas from 'classes-students/edit/editClassSagas';
import * as configurationSagas from 'configuration/configurationSagas';
import * as controlPanelSagas from 'control-panel/controlPanelSagas';
import * as profilesSagas from 'profiles/profilesSagas';
import * as addProfileSagas from 'add-profile/addProfileSagas';
import * as registerSagas from 'register/registerSagas';
import * as reportsSagas from 'reports/reportsSagas';
import * as reportApplicationSagas from 'reports/application/reportApplicationSagas';
import * as reportGeneralPanelSagas from 'reports/general-panel/reportGeneralPanelSagas';
import * as reportGeneralPanelTRISagas from 'reports/general-panel-tri/reportGeneralPanelTRISagas';
import * as reportApplicationDetailsSagas from 'reports/application-detail/reportApplicationDetailsSagas';
import * as simulatedSagas from 'simulated/simulatedSagas';
import * as simulatedContentMapSagas from 'simulated/content-map/simulatedContentMapSagas';
import * as simulatedCorrectionSagas from 'simulated/correction/simulatedCorrectionSagas';
import * as simulatedQuestionsSagas from 'simulated/questions/simulatedQuestionsSagas';
import * as simulatedEditionSagas from 'simulated/simulated-edition/simulatedEditionSagas';

import * as studentSimulatedSagas from 'student-simulated/studentSimulatedSagas';
import * as onlineSimulatedSagas from 'online-simulated/onlineSimulatedSagas';

import * as unitsSagas from 'units/unitsSagas';
import * as changePasswordSagas from 'changePassword/changePasswordSagas';

import * as composingCorrectionSagas from 'simulated/composing-correction/composingCorrectionSagas';
import * as managerDashboardSagas from 'reports/manager-dashboard/managerDashboardSagas';
import * as networkPanelSagas from 'reports/network-panel/networkPanelSagas';

import omit from 'lodash/omit';

/**
 * Reusable generator for fetch entities using REQUEST, SUCCESS AND FAILURE
 * suffixes to identify transitions
 * */

export function* genericApiTask(
  prefix: string,
  apiFn: () => Promise<any>,
  apiUrl: string,
  apiArgs: any,
  actionArgs: any,
): Iterable<T> {
  try {
    const pureApiArgs = apiArgs instanceof FormData ? apiArgs : omit(apiArgs, ['resolve, reject']);
    const { resolve } = apiArgs || {};
    yield put(action(request(prefix), actionArgs));
    const { data } = yield call(apiFn, apiUrl, pureApiArgs);
    yield put(action(success(prefix), { ...actionArgs, payload: data }));
    if (resolve) {
      resolve({ data });
    }
    return { data };
  } catch (error) {
    yield put(
      action(failure(prefix), {
        ...actionArgs,
        error,
        payload: error && error.response ? error.response.data : {},
      }),
    );
    const { reject } = apiArgs || {};
    if (reject) {
      reject(error);
    }
    return { error };
  }
}

export function* genericWatchApiTask(actionType: string, task: Generator) {
  while (true) {
    const { payload } = yield take(actionType);
    yield fork(task, payload);
  }
}

// bind the generic generator
export function apiTask(actionType: string, apiFn: () => Promise<any>, ...args: any) {
  return genericApiTask.bind(null, actionType, apiFn, ...args);
}

export function watchApiTask(actionType: string, apiFn: () => Promise<any>, ...args: any) {
  const task = genericApiTask.bind(null, actionType, apiFn, ...args);
  return genericWatchApiTask.bind(null, actionType, task);
}

// only register the root module saga
export default function* rootSaga(): Iterable<T> {
  yield all([
    fork(appSagas.sagas),
    fork(loginSagas.sagas),
    fork(registerSagas.sagas),
    fork(configurationSagas.sagas),
    fork(controlPanelSagas.sagas),
    fork(classesStudentsSagas.sagas),
    fork(profilesSagas.sagas),
    fork(addProfileSagas.sagas),
    fork(reportsSagas.sagas),
    fork(simulatedSagas.sagas),
    fork(unitsSagas.sagas),
    fork(simulatedContentMapSagas.sagas),
    fork(simulatedCorrectionSagas.sagas),
    fork(editClassSagas.sagas),
    fork(simulatedQuestionsSagas.sagas),
    fork(simulatedEditionSagas.sagas),
    fork(studentSimulatedSagas.sagas),
    fork(reportGeneralPanelSagas.sagas),
    fork(reportApplicationDetailsSagas.sagas),
    fork(reportApplicationSagas.sagas),
    fork(changePasswordSagas.sagas),
    fork(reportGeneralPanelTRISagas.sagas),
    fork(onlineSimulatedSagas.sagas),
    fork(composingCorrectionSagas.sagas),
    fork(managerDashboardSagas.sagas),
    fork(networkPanelSagas.sagas),
  ]);
}
